import { useCallback, useEffect, useState } from "react";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth, useOutletApi } from "@/features/authentication";
import {
  ExpenseAccrualDetailsSection,
  GeneralInformationSection,
  PaymentHistorySection,
} from "@/features/expense-accruals/expense-accrual";
import { PermissionEnum } from "@/features/permissions";
import { getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  CustomDialogTitle,
  JournalDetailsDialog,
  PageContainer,
  ResponsiveToolbarButton,
  useSnackbar,
} from "@/features/ui";

export const ExpenseAccrual = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, hasPermission } = useAuth();
  const outletApi = useOutletApi();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [expenseAccrual, setExpenseAccrual] = useState(null);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const loadExpenseAccrual = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await outletApi.get(`expense-accruals/${referenceNumber}`, {
        params: { withPayments: true },
      });
      setExpenseAccrual(data);
    } catch (error) {
      setError(getErrorMessage(error, "Gagal memuat pencatatan beban"));
    } finally {
      setLoading(false);
    }
  }, [outlet, outletApi, referenceNumber]);

  useEffect(() => {
    loadExpenseAccrual();
  }, [loadExpenseAccrual]);

  const handleDelete = async () => {
    if (!expenseAccrual || !outlet) return;

    try {
      setIsDeleting(true);
      await outletApi.delete(`expense-accruals/${referenceNumber}`, {
        headers: { "Outlet-Id": outlet.id },
      });
      showSnackbar("Pencatatan beban berhasil dihapus", "success");
      navigate("/expense/expense-accruals");
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal menghapus pencatatan beban"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // The backend rejects an edit or a delete once any payment is allocated
  // against this document; hide the buttons rather than let it 409.
  const hasAllocation = (expenseAccrual?.paidAmount ?? 0) > 0;

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.DELETE_EXPENSE_ACCRUALS) &&
        !hasAllocation && (
          <ResponsiveToolbarButton
            label="Hapus"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          />
        )}
      {hasPermission(PermissionEnum.UPDATE_EXPENSE_ACCRUALS) &&
        !hasAllocation && (
          <ResponsiveToolbarButton
            label="Ubah"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate("edit")}
          />
        )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
    </>
  );

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Pencatatan Beban"
      breadcrumbs={[
        { title: "Pencatatan Beban", path: "/expense/expense-accruals" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection expenseAccrual={expenseAccrual} />
        <ExpenseAccrualDetailsSection
          expenseAccrualDetails={expenseAccrual.expenseAccrualDetails || []}
        />
        <PaymentHistorySection expenseAccrual={expenseAccrual} />

        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="EXPENSE_ACCRUAL"
          transactionableId={expenseAccrual?.id}
        />

        {deleteDialogOpen && (
          <Dialog
            open
            onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <CustomDialogTitle
              title="Hapus Pencatatan Beban"
              onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            />
            <DialogContent>
              <DialogContentText>
                Apakah Anda yakin ingin menghapus pencatatan beban{" "}
                <strong>{referenceNumber}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Batal
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus..." : "Hapus"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </PageContainer>
  );
};
